import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TONE = { due: "text-amber-200", overdue: "text-destructive", paid: "text-neon-lime", void: "text-muted-foreground", refunded: "text-muted-foreground" };

function Applications({ refresh }) {
  const { data } = useQuery({ queryKey: ["ppApps"], queryFn: () => api.get("/admin/post-payment/applications").then((r) => r.data) });
  const set = async (id, status) => {
    try {
      await api.post(`/admin/post-payment/riders/${id}`, { status, note: status === "approved" ? null : window.prompt("Note (optional)") || null });
      toast.success(`Post-payment ${status}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="space-y-2 rounded-xl border bg-card p-4" data-testid="pp-applications">
      <h2 className="font-semibold">Post-payment applications</h2>
      {!data?.length && <p className="text-sm text-muted-foreground">No applications yet.</p>}
      {data?.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 py-2 text-sm" data-testid={`pp-app-${a.id}`}>
          <span>{a.rider_name} · {a.rider_email} · {when(a.created_at)} · <span className="font-semibold uppercase">{a.status}</span></span>
          <span className="flex gap-1.5">
            {["approved", "declined", "suspended"].map((s) => (
              <Button key={s} size="sm" variant={s === "approved" ? "default" : "ghost"} disabled={a.status === s} onClick={() => set(a.rider_id, s)} data-testid={`pp-${s}-${a.id}`}>
                {s === "approved" ? "Approve" : s === "declined" ? "Decline" : "Suspend"}
              </Button>
            ))}
          </span>
        </div>
      ))}
    </section>
  );
}

function Records({ refresh }) {
  const [status, setStatus] = useState("");
  const { data } = useQuery({ queryKey: ["payRecords", status], queryFn: () => api.get("/admin/payments", { params: { status: status || undefined } }).then((r) => r.data) });
  const set = async (id, s) => {
    try { await api.post(`/admin/payments/${id}`, { status: s }); toast.success(`Marked ${s}`); refresh(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="space-y-2 rounded-xl border bg-card p-4" data-testid="payment-records">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Post-payment records</h2>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border bg-background px-2 text-xs" data-testid="payment-status-filter">
          <option value="">All</option>{Object.keys(TONE).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {!data?.length && <p className="text-sm text-muted-foreground">No post-payment rides yet.</p>}
      {data?.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 py-2 text-sm" data-testid={`payment-record-${r.id}`}>
          <span>{r.rider_name} · {money(r.amount)} · due {when(r.due_date)} · <span className={`font-semibold uppercase ${TONE[r.status]}`}>{r.status}</span>{r.paid_at ? ` · paid ${when(r.paid_at)}` : ""}</span>
          <span className="flex gap-1">
            {["paid", "void", "refunded"].map((s) => <Button key={s} size="sm" variant="ghost" disabled={r.status === s} onClick={() => set(r.id, s)} data-testid={`payment-${s}-${r.id}`}>{s}</Button>)}
          </span>
        </div>
      ))}
    </section>
  );
}

function Settings() {
  const { data, refetch } = useQuery({ queryKey: ["billingSettings"], queryFn: () => api.get("/admin/billing/settings").then((r) => r.data) });
  const [draft, setDraft] = useState(null);
  const s = draft || data;
  if (!s) return null;
  const save = async () => {
    try {
      await api.put("/admin/billing/settings", { ...s, credit_presets: String(s.credit_presets).split(",").map(Number).filter((n) => n > 0) });
      toast.success("Settings saved"); setDraft(null); refetch();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="flex flex-wrap items-end gap-3 rounded-xl border bg-card p-4" data-testid="billing-settings">
      <label className="text-xs">Post-payment due day
        <select value={s.post_payment_due_weekday} onChange={(e) => setDraft({ ...s, post_payment_due_weekday: Number(e.target.value) })} className="mt-1 block h-9 rounded-md border bg-background px-2 text-sm" data-testid="settings-due-day">
          {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select></label>
      <label className="text-xs">Due hour (Central)
        <Input type="number" min="0" max="23" value={s.post_payment_due_hour} onChange={(e) => setDraft({ ...s, post_payment_due_hour: Number(e.target.value) })} className="mt-1 h-9 w-24" data-testid="settings-due-hour" /></label>
      <label className="text-xs">Credit amounts riders can pick
        <Input value={String(s.credit_presets)} onChange={(e) => setDraft({ ...s, credit_presets: e.target.value })} className="mt-1 h-9 w-56" data-testid="settings-presets" /></label>
      <Button onClick={save} disabled={!draft} data-testid="settings-save">Save</Button>
    </section>
  );
}

export default function AdminPayments() {
  const qc = useQueryClient();
  const refresh = () => ["ppApps", "payRecords", "billingSummary"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  return (
    <div className="space-y-5">
      <PageHead title="Payments" sub="Pay at Pickup, Post-Payment and outstanding balances. Booking fee is always $0." />
      <Applications refresh={refresh} />
      <Records refresh={refresh} />
      <Settings />
    </div>
  );
}
